/* eslint-disable max-len */
import React, { useCallback, useMemo, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import {
  CategoryScale,
  Chart as ChartJS,
  type ChartData,
  type ChartOptions,
  Filler,
  type FontSpec,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
  type TooltipOptions,
} from 'chart.js';
import { merge } from 'chart.js/helpers';
import 'chartjs-adapter-moment';
import _ from 'lodash';
import moment from 'moment/moment';
import { produce } from 'immer';
import { type InnerTooltipDetailHTML } from '@bbkAdminComponents/tooltips/detail-tooltip-inner-html';
import { BrightbackChartHex } from '@bbkAdminComponents/indicators/indicator-colors';
import { formatCurrencyOrNumber } from '@bbkAdminComponents/predictive/customer-risk-level-widget';
import tailwindColors from '@bbkShared/tailwind.colors';
import { ChartTooltip } from '@bbkAdminComponents/charts/chart-tooltip';
import { cs } from '@bbkAdminUtils/css-utils';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { useGetDemoSuffix } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { selectExperiencesAudiencesReportFilter } from '@bbkAdminRedux/rtkq/app-persist/reports-filter';
import {
  type GetReportV2Args,
  type LifecycleEntityOfferMetricsReport,
  reportsSlice,
  ReportType,
} from '@bbkAdminRedux/rtkq/reports.slice';
import { useGetGroupByPeriod } from '@bbkAdminRedux/rtkq/reports.utils';
import { selectFromResultTyped } from '@bbkAdminRedux/redux_utils';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { useSelectAllOffers } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import Icons from '@bbkAdminUtils/icons';
import { formatPercents } from '@bbkAdminComponents/settings/plans/components';
import { useShowPlayDemoData } from '@bbkAdminComponents/play/play-context';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  TimeScale,
  Legend,
  Filler
);

ChartJS.defaults.font.family =
  "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI','Helvetica Neue', Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji','Segoe UI Symbol'";
const fontColor = tailwindColors.cbrneutral['800'];
const scaleFontColor = tailwindColors.cbrneutral['400'];
const fontStyle: Partial<FontSpec> = {
  size: 14,
  weight: 500,
};

const dataSample = {
  labels: ['Apr 8', 'Apr 15', 'Apr 22', 'Apr 29', 'May 6', 'May 13'],
  datasets: [
    {
      label: 'New Advanced Plan – Agent Resolutions',
      data: [0, 100000, 170000, 270000, 330000, 370000],
      borderColor: BrightbackChartHex[0].hex,
      backgroundColor: `${BrightbackChartHex[0].hex}0D`,
      fill: true,
      tension: 0.4,
    },
    {
      label: 'Advanced Plan – Agent Conversions',
      data: [0, 70000, 80000, 110000, 130000, 150000],
      borderColor: BrightbackChartHex[1].hex,
      backgroundColor: `${BrightbackChartHex[2].hex}0D`,
      fill: true,
      tension: 0.4,
    },
    {
      label: 'No Offer (Control)',
      data: [0, 5000, 10000, 15000, 20000, 25000],
      borderColor: BrightbackChartHex[2].hex,
      backgroundColor: `${BrightbackChartHex[2].hex}0D`,
      fill: true,
      tension: 0.4,
    },
  ],
};

const defaultOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  resizeDelay: 20,
  interaction: {
    mode: 'nearest',
    intersect: false,
  },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        pointStyle: 'circle',
        usePointStyle: true,
        font: fontStyle,
        color: fontColor,
      },
    },
    title: { display: false },
    tooltip: {
      titleFont: fontStyle,
      bodyFont: fontStyle,
      titleColor: fontColor,
      bodyColor: fontColor,
    },
  },
  scales: {
    y: {
      ticks: {
        // callback: (value) => `$${Number(value) / 1000}K`,
        font: fontStyle,
        color: scaleFontColor,
        maxTicksLimit: 10,
      },
      suggestedMin: 0,
    },
    x: {
      type: 'time',
      time: {
        round: 'day',
        tooltipFormat: 'll HH:mm',
        unit: 'day',
        // unit: currentGrouping.id,
        displayFormats: {
          week: 'M/D',
        },
      },
      grid: {
        drawTicks: true,
        display: false,
      },
      ticks: {
        font: fontStyle,
        color: scaleFontColor,
        maxTicksLimit: 12,
      },
    },
  },
};

type TooltipProps = React.ComponentProps<typeof InnerTooltipDetailHTML>;
type MetricName =
  keyof LifecycleEntityOfferMetricsReport['entity_report_metrics'][number]['trends'][number];
type AllowedMetric = Extract<
  MetricName,
  | 'total_customers'
  | 'fulfillments'
  | 'fulfillment_rate'
  | 'engagements'
  | 'accept_rate'
  | 'revenue'
>;
export const PlayMetricChart: React.FC<{
  playId: string;
  metric: AllowedMetric;
}> = ({ playId, metric }) => {
  const appKey = useSelector(selectAppId);
  const { startDate: start, endDate: end } = useSelectDatePicker();
  const suffix = useGetDemoSuffix();
  const reportFilters = useSelector(
    selectExperiencesAudiencesReportFilter(suffix)
  );
  const showDemoData = useShowPlayDemoData();
  const groupByPeriod = useGetGroupByPeriod();
  const requestEntityMetrics: GetReportV2Args['report_type_requests'][number] =
    {
      report_type: ReportType.lifecycle_entity_metrics,
      parameters: {
        entity_type: 'offer',
        group_by: 'offer_id',
        group_by_period: groupByPeriod,
      },
    };
  const argsEntityMetrics: GetReportV2Args = {
    appKey,
    start,
    end,
    report_type_requests: [requestEntityMetrics],
    filters: [
      reportFilters,
      [{ field: 'play_id', operator: 'equals', value: playId }],
    ],
    context: showDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  };
  const optsEntityMetrics = {
    skip: !appKey || !start || !end,
    selectFromResult: selectFromResultTyped<LifecycleEntityOfferMetricsReport>,
  };
  const { data: offerEntityMetrics, isFetching: isOfferEntityMetricsFetching } =
    reportsSlice.endpoints.getReportV2.useQuery(
      argsEntityMetrics,
      optsEntityMetrics
    );

  const offersArgs = {
    appKey,
    context: showDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  };
  const offers = useSelectAllOffers(offersArgs);

  const labels = useMemo(
    () =>
      offerEntityMetrics?.entity_report_metrics[0]?.trends.map((t) => t.date) ??
      [],
    [offerEntityMetrics?.entity_report_metrics]
  );
  const datasets = useMemo(
    () =>
      offerEntityMetrics?.entity_report_metrics.map((x, idx) => {
        const color = BrightbackChartHex[idx % BrightbackChartHex.length]!;
        return {
          label: x.no_target
            ? 'No offer'
            : (offers.find((o) => o.id === x.entity_id)?.display_name ??
              `Entity ${x.entity_id}`),
          data: x.trends.map((dp) => {
            const val = dp[metric];
            if (metric === 'accept_rate' || metric === 'fulfillment_rate') {
              return val / 100;
            }
            return val;
          }),
          borderColor: color.hex,
          backgroundColor: `${color.hex}AA`,
          fill: true,
          tension: 0.4,
        };
      }) ?? [],
    [metric, offerEntityMetrics?.entity_report_metrics, offers]
  );

  const data: ChartData<'line', number[], string> = useMemo(
    () => ({ datasets, labels }),
    [datasets, labels]
  );

  const [tooltipStyles, setTooltipStyles] = useState<React.CSSProperties>();
  const [tooltipProps, setTooltipProps] = useState<TooltipProps>();

  const turnOffTooltip = useCallback(() => {
    setTooltipStyles(undefined);
    setTooltipProps(undefined);
  }, []);

  const externalTooltipHandler: TooltipOptions['external'] = useCallback(
    ({ chart, tooltip }) => {
      if (tooltip.opacity === 0 || !tooltip.body) {
        turnOffTooltip();
        return;
      }

      const dataPointIndex = _.get(tooltip, 'dataPoints[0].dataIndex');
      const dataPointDatasetIndex = _.get(
        tooltip,
        'dataPoints[0].datasetIndex'
      );

      const dataset = data.datasets[dataPointDatasetIndex];
      if (!dataset) return;
      const label = data.labels![dataPointIndex];
      const tooltipTitle = `${moment(label).format('MMMM DD, YYYY')}`;
      const formatFn = (val: number) =>
        metric === 'revenue'
          ? formatCurrencyOrNumber(val, true)
          : metric === 'accept_rate' || metric === 'fulfillment_rate'
            ? formatPercents(val)
            : formatCurrencyOrNumber(val);
      setTooltipProps({
        tipTitle: {
          text: tooltipTitle,
        },
        stats: data.datasets.map((d, idx) => ({
          indicatorColor:
            BrightbackChartHex[idx % BrightbackChartHex.length]!.key,
          statContainerType: 'StatStacked',
          label: d.label!,
          value: formatFn(d.data[dataPointIndex]!),
          valueClasses: 'tw-text-cbrneutral-700',
        })),
      });

      const position = chart.canvas.getBoundingClientRect();

      setTooltipStyles({
        left: `${position.left + window.pageXOffset + tooltip.caretX}px`,
        top: `${position.top + window.pageYOffset + tooltip.caretY}px`,
      });
    },
    [data.datasets, data.labels, metric, turnOffTooltip]
  );

  const options = useMemo(() => {
    const revenueMetricOpts: ChartOptions<'line'> = {
      scales: {
        y: {
          ticks: {
            callback: (value) => formatCurrencyOrNumber(value as number, true),
          },
        },
      },
    };
    const acceptRateMetricOpts: ChartOptions<'line'> = {
      scales: {
        y: {
          ticks: {
            callback: (value) => formatPercents(value as number),
          },
        },
      },
    };
    const metricOpts =
      metric === 'revenue'
        ? revenueMetricOpts
        : metric === 'accept_rate' || metric === 'fulfillment_rate'
          ? acceptRateMetricOpts
          : {};

    const customOpts: ChartOptions<'line'> = {
      plugins: {
        tooltip: {
          position: 'nearest',
          enabled: false,
          external: externalTooltipHandler,
        },
      },
    };
    return produce(defaultOptions, (draft) =>
      merge(draft, [customOpts, metricOpts])
    );
  }, [externalTooltipHandler, metric]);

  return (
    <div
      className={cs(
        'tw-h-96 tw-w-full tw-p-6',
        'tw-rounded-lg tw-border tw-bg-white'
      )}
    >
      {isOfferEntityMetricsFetching ? (
        <div
          className={cs(
            'tw-flex tw-items-center tw-justify-center',
            'tw-h-full tw-w-full tw-rounded-lg tw-bg-cbrneutral-100 tw-text-xl',
            '[&_svg]:tw-h-10 [&_svg]:tw-w-10'
          )}
        >
          {Icons.cbrLoader} Loading...
        </div>
      ) : (
        <Chart type="line" data={data} options={options} />
      )}
      {Boolean(tooltipProps) &&
        createPortal(
          <ChartTooltip styles={tooltipStyles} tooltipProps={tooltipProps} />,
          document.getElementById('chartjs-portal')!
        )}
    </div>
  );
};
